import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useSignerStore } from '../store/signer.store';
import { useFormContent } from '../hooks/use-form-content.hook';

import { FormNavigation } from './navigation/FormNavigation';
import { FormSection } from './FormSection';
import { logger } from '@/features/help/utils/logger';
import { ApplicationSidebar } from './sidebar/ApplicationSidebar';
import { MobileProgressHeader } from './navigation/MobileProgressHeader';
import { TermsConsentField } from './fields/TermsConsentField';

// Purpose: Main component that handles form content rendering with sidebar layout
export const FormContent = () => {
  const { mappingShortId } = useParams<{ mappingShortId: string }>();
  const { structure, viewableFields, termsConsent } = useApplicationStore();
  const { activePage, getCurrentPage, getTotalPages } = useNavigationStore();
  const { currentSigner } = useSignerStore();

  // Animation state
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use custom hook for all form content logic
  const {
    isTermsInvalid,
    handleTermsConsentChange,
    handleNextPageWithValidation,
    handlePreviousPageNavigation,
    formRef,
  } = useFormContent({ mappingShortId });

  // Trigger animation on mount and page changes
  useEffect(() => {
    setIsVisible(false); // Reset animation

    // Scroll to top on page navigation (but not on initial load)
    if (!isInitialLoad) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const timer = setTimeout(
      () => {
        setIsVisible(true);
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      },
      isInitialLoad ? 50 : 150
    ); // Longer delay for page transitions
    return () => clearTimeout(timer);
  }, [activePage, isInitialLoad]); // Re-run when activePage changes

  const currentPage = getCurrentPage();
  const totalPages = getTotalPages();

  // Check if this is the last page of the form
  const isLastPage = activePage === totalPages - 1;

  // Safety check - although ApplicationForm should prevent this
  if (!structure?.theme || !currentPage?.sections) {
    logger.debug('FormContent', 'Missing required data', {
      hasTheme: !!structure?.theme,
      hasSections: !!currentPage?.sections,
      currentPage,
    });
    return null;
  }

  // Filter out sections that have no viewable fields for the current signer
  const visibleSections = currentPage.sections.filter((section) =>
    section.fields.some((field) => viewableFields.includes(field.id))
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <ApplicationSidebar />

      {/* Mobile Progress Header */}
      <MobileProgressHeader />

      {/* Main Content */}
      <div className="flex-1 ml-0 md:ml-80 lg:ml-72 xl:ml-96 transition-all duration-300 font-inter flex flex-col">
        {/* Invisible spacer to match logo height */}
        <div className="bg-white hidden md:block h-32" />

        <div
          ref={formRef}
          className={`bg-white px-6 py-6 md:py-8 md:pt-6 mt-36 md:mt-0 flex-1 flex flex-col transition-all ease-out md:pl-12 ${
            isInitialLoad ? 'duration-500' : 'duration-700'
          } ${
            isVisible
              ? 'opacity-100 translate-x-0'
              : isInitialLoad
                ? 'opacity-0 -translate-x-1'
                : 'opacity-0 -translate-x-2'
          }`}
        >
          {/* Form Content */}
          <div className="min-h-0">
            {visibleSections.map((section) => (
              <FormSection key={section.id} section={section} />
            ))}

            {/* Additional Signer Info Note - only show on contact-info page for primary signer */}
            {currentPage.id === 'contact-info' && currentSigner === 1 && (
              <div className="mt-6 mb-2">
                <div className="flex flex-col lg:flex-row justify-start">
                  <div className="lg:w-64 flex-shrink-0"></div>
                  <div className="flex-1 lg:pl-8 max-w-4xl">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Need to add additional signers?</span> Sign and submit this application first, and you'll be prompted to enter their email addresses. We'll send them a secure link to complete their portion of the application.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms Consent Field on the last page */}
            {isLastPage && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col lg:flex-row justify-start">
                  <div className="lg:w-64 flex-shrink-0"></div>
                  <div className="flex-1 lg:pl-8 max-w-4xl">
                    <TermsConsentField
                      onChange={handleTermsConsentChange}
                      value={termsConsent}
                      isInvalid={isTermsInvalid}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-row justify-start">
              <div className="lg:w-64 flex-shrink-0"></div>
              <div className="flex-1 lg:pl-8 max-w-4xl">
                <FormNavigation
                  activePage={activePage}
                  totalPages={totalPages}
                  theme={structure.theme}
                  onNext={() => handleNextPageWithValidation(isLastPage, formRef)}
                  onBack={handlePreviousPageNavigation}
                  mappingId={mappingShortId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
